import Phaser from "phaser";
import { HealthBar } from "../ui/health-bar";
import { Item, getHatSpriteKey } from "./common";

// Hat bobs down this many pixels (scaled with the bot) while the second walk
// frame is shown, in sync with the 2-frame body animation, then back up.
const HAT_BOB_OFFSET = 1;
// While moving, the body and the hat hop this many pixels (scaled) up and back down
// together, on top of the hat's own frame bob. Matches the player's walk hop.
const WALK_BOB_OFFSET = 2;
// Duration of one leg of that hop (ms); a full up-and-down cycle takes twice as long.
const WALK_BOB_DURATION = 250;
// How long the hop takes to ease back down to rest when the enemy stops (ms), instead
// of snapping the body down from wherever mid-hop it happened to be.
const WALK_BOB_SETTLE_DURATION = 120;

export type EnemyContainerResult = {
  container: Phaser.GameObjects.Container;
  sprite: Phaser.GameObjects.Sprite;
  weaponSprite?: Phaser.GameObjects.Sprite;
  hatSprite?: Phaser.GameObjects.Sprite;
  healthbar: HealthBar;
  aggroText?: Phaser.GameObjects.Text;
  behaviorText?: Phaser.GameObjects.Text;
  /** Run the up/down walk hop on the body and hat while the enemy is moving. */
  setMoving: (moving: boolean) => void;
};

export type EnemyContainerOptions = {
  scene: Phaser.Scene;
  x: number;
  y: number;
  id: string;
  name?: string;
  texture: string;
  health: number;
  maxHealth: number;
  showAggro?: boolean;
  scale?: number;
  spriteSize?: number; // Sprite frame size in pixels
  weapon?: Item; // Add weapon data
  hat?: number; // hats.json id, for bots that spawned with a random hat
};

export class Enemy {
  health: number;
  position: { x: number; y: number };
  behaviorText?: Phaser.GameObjects.Text;
  container?: Phaser.GameObjects.Container;
  scene?: Phaser.Scene;

  constructor(health: number, position: { x: number; y: number }) {
    this.health = health;
    this.position = position;
  }

  /**
   * Call this after creating the enemy to set up debug AI state text visibility.
   * Can be called at any time to update visibility based on admin mode.
   */
  public updateBehaviorText(adminMode: boolean) {
    if (!this.behaviorText) return;
    this.behaviorText.setVisible(adminMode);
  }

  static createContainer(options: EnemyContainerOptions): EnemyContainerResult {
    const {
      scene,
      x,
      y,
      id,
      name,
      texture,
      health,
      maxHealth,
      showAggro,
      scale = 1,
      weapon,
      hat,
    } = options;
    const container = scene.add.container(x, y);
    // Set depth based on Y position for proper rendering order
    // Use 1000 + y to keep above static tile layers (0-2) but allow Y-sorting
    container.setDepth(1000 + y);
    const sprite = scene.add.sprite(0, 0, texture);

    // For 64x64 sprites (goboom, mutant_lord), set origin to bottom-center
    // For 32x32 sprites, use center origin
    if (texture === "enemy_goboom" || texture === "enemy_mutant_lord") {
      sprite.setOrigin(0.5, 1.0); // Center-bottom
    } else {
      sprite.setOrigin(0.5, 0.5); // Center-center
    }

    sprite.setScale(scale); // Apply scale to sprite

    // Adjust sprite Y position so bottom of sprite stays at container center for larger scales
    // When scale > 1, shift sprite up by (scale - 1) * half sprite height
    if (scale > 1) {
      const spriteHeight = sprite.height;
      const yOffset = -(scale - 1) * (spriteHeight / 2);
      sprite.setY(yOffset);
    }

    container.add(sprite);

    // Create weapon sprite if weapon is provided (for bots/bosses)
    let weaponSprite: Phaser.GameObjects.Sprite | undefined;
    if (weapon && weapon.definition.spriteSheet === "weapon") {
      // Calculate frame index based on sprite coordinates
      const columns = 10; // weapon spritesheet has 10 columns
      const frame =
        weapon.definition.spriteY * columns + weapon.definition.spriteX;

      // Position weapon at 16px from bottom of sprite
      // For center-origin sprites: spriteHeight/2 - 16
      // For bottom-origin sprites: -16
      // After sprite Y adjustment for scale, weapon needs same adjustment
      let weaponYPos: number;
      if (texture === "enemy_goboom" || texture === "enemy_mutant_lord") {
        // Bottom-origin sprites (64x64)
        weaponYPos = -16 * scale;
      } else {
        // Center-origin sprites (32x32)
        const spriteHeight = sprite.height;
        weaponYPos = (spriteHeight / 2 - 16) * scale;
      }

      // Apply the same Y offset as the sprite if scale > 1
      if (scale > 1) {
        const spriteHeight = sprite.height;
        const yOffset = -(scale - 1) * (spriteHeight / 2);
        weaponYPos += yOffset;
      }

      weaponSprite = scene.add.sprite(0, weaponYPos, "weapon", frame);
      weaponSprite.setOrigin(0, 0.5); // Set origin to left side (0) and vertical center (0.5)
      weaponSprite.setScale(scale); // Scale weapon sprite to match enemy size
      weaponSprite.setVisible(true);
      // Weapon depth is controlled by container depth, not individual sprite depth
      container.add(weaponSprite);
    }

    // Create hat sprite if this enemy (bot) spawned with a random hat
    let hatSprite: Phaser.GameObjects.Sprite | undefined;
    const hatKey = getHatSpriteKey(hat);
    if (hatKey && scene.textures.exists(hatKey)) {
      // Position hat at the top of the sprite, same reasoning as weaponYPos above
      let hatYPos: number;
      if (texture === "enemy_goboom" || texture === "enemy_mutant_lord") {
        // Bottom-origin sprites (64x64): head is at the top of the frame
        const spriteHeight = sprite.height;
        hatYPos = -spriteHeight * scale;
      } else {
        // Center-origin sprites (32x32): head is at the top half of the frame
        const spriteHeight = sprite.height;
        hatYPos = -(spriteHeight / 2) * scale;
      }

      // Apply the same Y offset as the sprite if scale > 1
      if (scale > 1) {
        const spriteHeight = sprite.height;
        const yOffset = -(scale - 1) * (spriteHeight / 2);
        hatYPos += yOffset;
      }

      // Lower the hat 8px so it doesn't hover above the bot's head
      hatYPos += 16 * scale;

      hatSprite = scene.add.sprite(0, hatYPos, hatKey, 0);
      hatSprite.setOrigin(0.5, 1); // Bottom-center so it sits on top of the head
      hatSprite.setScale(scale);
      container.add(hatSprite);
    }

    // Resting positions the walk hop is measured from.
    const spriteBaseY = sprite.y;
    const hatBaseY = hatSprite ? hatSprite.y : 0;
    // The hat's own 1px (scaled) bob off the body animation's frame changes, and the
    // up/down hop that runs while the enemy is moving. They compose onto the hat, so
    // both are written through applyBob instead of assigning y directly.
    let hatFrameBob = 0;
    const walkBob = { offset: 0 };
    const bobHat = hatSprite;
    const applyBob = () => {
      if (!sprite.active) return;
      sprite.y = spriteBaseY + walkBob.offset;
      if (bobHat?.active) bobHat.y = hatBaseY + hatFrameBob + walkBob.offset;
    };

    if (bobHat) {
      // Bob the hat down on the second walk frame, back up on the first, keeping it in
      // sync with the body's 2-frame animation.
      const hatBobOffset = HAT_BOB_OFFSET * scale;
      sprite.on(
        Phaser.Animations.Events.ANIMATION_UPDATE,
        (
          _animation: Phaser.Animations.Animation,
          frame: Phaser.Animations.AnimationFrame,
        ) => {
          hatFrameBob = frame.index === 2 ? hatBobOffset : 0;
          applyBob();
        },
      );
    }

    let walkBobTween: Phaser.Tweens.Tween | undefined;
    let walkBobSettleTween: Phaser.Tweens.Tween | undefined;
    const clearWalkBobSettle = () => {
      if (!walkBobSettleTween) return;
      walkBobSettleTween.stop();
      walkBobSettleTween.destroy();
      walkBobSettleTween = undefined;
      walkBob.offset = 0;
    };
    const setMoving = (moving: boolean) => {
      if (moving) {
        // Idempotent: the handler calls this every interpolation frame.
        if (walkBobTween) return;
        // A settle left over from a move that just ended would fight the loop, and the
        // loop has to start from a zero offset or its yoyo would swing around the wrong
        // midpoint.
        clearWalkBobSettle();
        walkBobTween = scene.tweens.add({
          targets: walkBob,
          offset: -WALK_BOB_OFFSET * scale,
          duration: WALK_BOB_DURATION,
          ease: "Sine.easeInOut",
          yoyo: true,
          repeat: -1,
          onUpdate: applyBob,
        });
        // The tween's first onUpdate is a frame away; write the current offset now.
        applyBob();
        return;
      }
      if (!walkBobTween) return;
      walkBobTween.stop();
      walkBobTween.destroy();
      walkBobTween = undefined;
      // Nothing to settle onto once the enemy is gone (the handler stops the hop on
      // removal), and the scene may be tearing its tween manager down with it.
      if (!sprite.active) {
        walkBob.offset = 0;
        return;
      }
      if (walkBob.offset === 0) return;
      // Ease the last of the hop out instead of dropping the body to rest in one frame.
      walkBobSettleTween = scene.tweens.add({
        targets: walkBob,
        offset: 0,
        duration: WALK_BOB_SETTLE_DURATION,
        ease: "Sine.easeOut",
        onUpdate: applyBob,
        onComplete: () => {
          walkBobSettleTween = undefined;
        },
      });
    };

    // Add walking animations for all enemy types (all have 2-frame spritesheets)
    if (texture === "enemy_bot") {
      // Create walking animation for bot if it doesn't exist
      if (scene.anims && !scene.anims.exists("bot-walk")) {
        scene.anims.create({
          key: "bot-walk",
          frames: scene.anims.generateFrameNumbers("enemy_bot", {
            start: 0,
            end: 1,
          }),
          frameRate: 6,
          repeat: -1,
        });
      }
      // Start the walking animation
      sprite.play("bot-walk");
    } else if (texture === "enemy_creepy") {
      // Create walking animation for creepy if it doesn't exist
      if (scene.anims && !scene.anims.exists("creepy-walk")) {
        scene.anims.create({
          key: "creepy-walk",
          frames: scene.anims.generateFrameNumbers("enemy_creepy", {
            start: 0,
            end: 1,
          }),
          frameRate: 5,
          repeat: -1,
        });
      }
      // Start the walking animation
      sprite.play("creepy-walk");
    } else if (texture === "enemy_goboom") {
      // Create walking animation for goboom if it doesn't exist
      if (scene.anims && !scene.anims.exists("goboom-walk")) {
        scene.anims.create({
          key: "goboom-walk",
          frames: scene.anims.generateFrameNumbers("enemy_goboom", {
            start: 0,
            end: 1,
          }),
          frameRate: 5,
          repeat: -1,
        });
      }
      // Start the walking animation
      sprite.play("goboom-walk");
    } else if (texture === "enemy_mutant_lord") {
      // Create walking animation for boss if it doesn't exist
      if (scene.anims && !scene.anims.exists("mutant-lord-walk")) {
        scene.anims.create({
          key: "mutant-lord-walk",
          frames: scene.anims.generateFrameNumbers("enemy_mutant_lord", {
            start: 0,
            end: 1,
          }),
          frameRate: 4,
          repeat: -1,
        });
      }
      // Start the walking animation
      sprite.play("mutant-lord-walk");
    } else if (texture === "enemy_scavenger") {
      // Create walking animation for scavenger if it doesn't exist
      if (scene.anims && !scene.anims.exists("scavenger-walk")) {
        scene.anims.create({
          key: "scavenger-walk",
          frames: scene.anims.generateFrameNumbers("enemy_scavenger", {
            start: 0,
            end: 1,
          }),
          frameRate: 5,
          repeat: -1,
        });
      }
      // Start the walking animation
      sprite.play("scavenger-walk");
    }

    /*
        // Enemy id for debug
        const idText = scene.add.text(0, -32, id, {
            font: '12px monospace',
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.5)',
            padding: { left: 2, right: 2, top: 1, bottom: 1 }
        });
        idText.setOrigin(0.5, 1);
        container.add(idText);
        */

    // Calculate effective scale based on sprite size for proper positioning
    // A 64px sprite with scale 1 should position like a 32px sprite with scale 2
    const spriteSize = options.spriteSize || 32;
    const effectiveScale = (spriteSize / 32) * scale;

    const healthbar = new HealthBar(
      scene,
      -20,
      -40 * effectiveScale,
      40,
      6,
      maxHealth,
      true,
      name,
      effectiveScale,
    ); // Pass effectiveScale for name text positioning
    healthbar.setHealth(health);
    container.add(healthbar.bar);
    container.add(healthbar.overlay);
    if (healthbar.nameText) {
      container.add(healthbar.nameText);
    }

    // Note: aggroText is dynamically managed in enemy-handler.ts based on aggro state changes
    // We don't create it here during initial container creation
    const aggroText = undefined;

    // Create behaviorText here
    const behaviorText = scene.add.text(0, -40 * effectiveScale, "", {
      font: "12px monospace",
      color: "#0f0",
      backgroundColor: "rgba(0,0,0,0.5)",
      padding: { left: 2, right: 2, top: 1, bottom: 1 },
    });
    behaviorText.setOrigin(0.5, 1);
    behaviorText.setVisible(false); // Default to hidden
    container.add(behaviorText);
    return {
      container,
      sprite,
      weaponSprite,
      hatSprite,
      healthbar,
      aggroText,
      behaviorText,
      setMoving,
    };
  }

  update() {
    // Update enemy state
  }

  attack() {
    // Enemy attack logic
  }

  /**
   * Rotate weapon sprite to point in a direction (for enemies with weapons)
   * @param weaponSprite The weapon sprite to rotate
   * @param direction Direction vector {x, y}
   * @param scale The scale factor of the enemy (defaults to 1)
   */
  static rotateWeapon(
    weaponSprite: Phaser.GameObjects.Sprite,
    direction: { x: number; y: number },
    scale: number = 1,
  ) {
    if (!weaponSprite || !weaponSprite.visible) return;

    const angle = Math.atan2(direction.y, direction.x);

    // Flip weapon sprite vertically if pointing left to avoid upside-down appearance
    if (direction.x < 0) {
      weaponSprite.setScale(scale, -scale);
      // When flipped, we need to subtract the angle offset instead
      weaponSprite.setRotation(angle - Math.PI / 4);
    } else {
      weaponSprite.setScale(scale, scale);
      // Add 45 degrees (π/4 radians) to correct for sprite being drawn pointing up-right
      weaponSprite.setRotation(angle + Math.PI / 4);
    }
  }
}
